/**
 * SalesFunnelTable Component
 *
 * Expandable section inside the Sales Department Analysis card showing the
 * per-salesperson Lead → Visit → Sale funnel over the past 5 working days,
 * with conversion ratios, active leads being worked, and appointments
 * scheduled over the next 3/5 CALENDAR days (today + 2 / today + 4 — the same
 * counting as the Sales Appointments 5-day outlook) — the auto-updating
 * replacement for the manual Excel sheet sales managers sent the CEO.
 *
 * Salespeople are grouped by SALES TEAM (team = sales manager, configured in
 * Configuration → Sales Teams) with per-team subtotals — the same bucketing
 * and row rendering (components/shared/SalesFunnelRows) as the Sales Team
 * Report, so this card and the report read identically. Column sorting
 * applies within each team block.
 *
 * Data loads lazily on first expand via useSalesFunnelData (GET /sales-funnel).
 */

import React, { useMemo, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { SalesFunnelRow, SalesTeamSection } from '../../services/api';
import { useSalesFunnelData } from '../../hooks/useSalesFunnelData';
import {
  EmptyTeamRow,
  MemberRow,
  TeamHeaderRow,
  TotalsRow,
  shortDate,
} from '../shared/SalesFunnelRows';

interface SalesFunnelTableProps {
  locationId: number;
}

type SortField =
  | 'name' | 'leads' | 'visits' | 'apptsPast' | 'sales'
  | 'visitsPerLead' | 'salesPerVisit' | 'salesPerLead'
  | 'activeLeads' | 'apptsNext3' | 'apptsNext5';
type SortDir = 'asc' | 'desc';

function sortValue(row: SalesFunnelRow, field: SortField, dir: SortDir): number | string {
  const nullFill = dir === 'asc' ? Infinity : -Infinity;
  switch (field) {
    case 'name': return row.name;
    case 'leads': return row.leads;
    case 'visits': return row.visits;
    case 'apptsPast': return row.apptsPast;
    case 'sales': return row.sales;
    case 'visitsPerLead': return row.visitsPerLeadPct ?? nullFill;
    case 'salesPerVisit': return row.salesPerVisitPct ?? nullFill;
    case 'salesPerLead': return row.salesPerLeadPct ?? nullFill;
    case 'activeLeads': return row.activeLeads ?? nullFill;
    case 'apptsNext3': return row.apptsNext3;
    case 'apptsNext5': return row.apptsNext5;
  }
}

export const SalesFunnelTable: React.FC<SalesFunnelTableProps> = ({ locationId }) => {
  const [expanded, setExpanded] = useState(false);
  const [sort, setSort] = useState<{ field: SortField; dir: SortDir }>({ field: 'sales', dir: 'desc' });
  const [detailRows, setDetailRows] = useState<Set<string>>(new Set());

  const { data, loading, error } = useSalesFunnelData({ locationId, enabled: expanded });

  const rowId = (team: SalesTeamSection, row: SalesFunnelRow) =>
    `${team.managerId}#${row.vinUserId ?? ''}#${row.dmsSalesRepId ?? ''}#${row.name}`;
  const toggleDetail = (key: string) => {
    setDetailRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Team sections with the current column sort applied within each team.
  // Falls back to a single Unassigned bucket if a stale backend response
  // predates the teams field.
  const sortedTeams = useMemo<SalesTeamSection[]>(() => {
    if (!data) return [];
    const teams: SalesTeamSection[] = data.teams
      ?? (data.rows.length > 0
        ? [{ managerId: '', managerName: 'Unassigned', rows: data.rows, subtotals: data.totals }]
        : []);
    const dir = sort.dir === 'asc' ? 1 : -1;
    return teams.map((team) => ({
      ...team,
      rows: [...team.rows].sort((a, b) => {
        const av = sortValue(a, sort.field, sort.dir);
        const bv = sortValue(b, sort.field, sort.dir);
        const cmp = typeof av === 'string'
          ? av.localeCompare(bv as string)
          : (av as number) - (bv as number);
        return cmp * dir;
      }),
    }));
  }, [data, sort]);

  function toggleSort(field: SortField) {
    setSort((prev) =>
      prev.field === field
        ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { field, dir: field === 'name' ? 'asc' : 'desc' },
    );
  }

  function sortIndicator(field: SortField): string {
    if (sort.field !== field) return '';
    return sort.dir === 'asc' ? ' ▲' : ' ▼';
  }

  const th = (label: string, field: SortField, extra = '') => (
    <th
      className={`px-3 py-2 text-right cursor-pointer select-none hover:text-gray-700 whitespace-nowrap ${extra}`}
      onClick={() => toggleSort(field)}
    >
      {label}{sortIndicator(field)}
    </th>
  );

  const windowSummary = data
    ? `${shortDate(data.pastDates[0])}–${shortDate(data.pastDates[data.pastDates.length - 1])} · next ${shortDate(data.nextDates[0])}–${shortDate(data.nextDates[data.nextDates.length - 1])}`
    : null;

  // Configured teams render even with zero activity (visible roster, honest
  // zeros — same as the report); the empty state only shows when there are
  // neither teams nor rows.
  const hasContent = sortedTeams.length > 0;

  return (
    <div className="mt-6 border border-gray-200 rounded-lg">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <h4 className="text-sm font-medium text-gray-700">
            Sales Funnel — Last 5 Working Days / Next 5 Days
          </h4>
          <span className="text-xs text-gray-500">
            {expanded && windowSummary ? windowSummary : 'per-salesperson leads → visits → sales, by team'}
          </span>
        </div>
        <ChevronDownIcon
          className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
        />
      </button>

      {expanded && (
        <div className="border-t border-gray-200">
          {error && (
            <div className="bg-red-50 border-b border-red-200 text-red-700 px-4 py-2 text-sm">{error}</div>
          )}
          {loading ? (
            <div className="p-6 text-center text-sm text-gray-500">Loading…</div>
          ) : !data || !hasContent ? (
            <div className="p-6 text-center text-sm text-gray-500">
              No funnel activity in this window yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr className="border-b border-gray-200">
                    <th className="px-3 py-1.5" />
                    <th colSpan={4} className="px-3 py-1.5 text-center font-semibold text-gray-600 border-l border-gray-200">
                      Last 5 Working Days
                    </th>
                    <th colSpan={3} className="px-3 py-1.5 text-center font-semibold text-gray-600 border-l border-gray-200">
                      Conversion
                    </th>
                    <th className="px-3 py-1.5 border-l border-gray-200" />
                    <th colSpan={2} className="px-3 py-1.5 text-center font-semibold text-gray-600 border-l border-gray-200">
                      Appts Scheduled
                      <span className="ml-1 font-normal normal-case text-gray-400">(includes today)</span>
                    </th>
                  </tr>
                  <tr>
                    <th
                      className="px-3 py-2 text-left cursor-pointer select-none hover:text-gray-700"
                      onClick={() => toggleSort('name')}
                    >
                      Team / Salesperson{sortIndicator('name')}
                    </th>
                    {th('Leads', 'leads', 'border-l border-gray-200')}
                    {th('Visits', 'visits')}
                    {th('Appts', 'apptsPast')}
                    {th('Sales', 'sales')}
                    {th('Visits/Lead', 'visitsPerLead', 'border-l border-gray-200')}
                    {th('Sales/Visit', 'salesPerVisit')}
                    {th('Sales/Lead', 'salesPerLead')}
                    {th('Active Leads', 'activeLeads', 'border-l border-gray-200')}
                    {th('Next 3', 'apptsNext3', 'border-l border-gray-200')}
                    {th('Next 5', 'apptsNext5')}
                  </tr>
                </thead>
                <tbody>
                  {sortedTeams.map((team) => (
                    <React.Fragment key={team.managerId || 'unassigned'}>
                      <TeamHeaderRow
                        managerId={team.managerId}
                        managerName={team.managerName}
                        rowCount={team.rows.length}
                      />
                      {team.rows.length === 0 ? (
                        <EmptyTeamRow />
                      ) : (
                        team.rows.map((row) => (
                          <MemberRow
                            key={rowId(team, row)}
                            row={row}
                            rowId={rowId(team, row)}
                            isOpen={detailRows.has(rowId(team, row))}
                            onToggle={toggleDetail}
                            nextDates={data.nextDates}
                            reportDate={data.reportDate}
                            reportDateIsToday
                          />
                        ))
                      )}
                      <TotalsRow
                        label={team.managerId ? `Team ${team.managerName} Subtotal` : 'Unassigned Subtotal'}
                        totals={team.subtotals}
                      />
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <TotalsRow label="Total" totals={data.totals} emphasis />
                </tfoot>
              </table>
              <div className="px-4 py-2 text-xs text-gray-500 bg-gray-50 border-t border-gray-200">
                Teams are configured in Configuration → Sales Teams; salespeople without a team
                appear under Unassigned. Last-5 working days follow the location's NEW-vehicle schedule
                (Sundays/holidays excluded); Next 3 / Next 5 are calendar days, matching the
                Sales Appointments 5-day outlook. Leads: VinSolutions Good Leads by origination date.
                Visits: completed showroom visits credited to the customer's salesperson (be-backs
                count). Sales: retail units from DealerBuilt (wholesale/swap excluded;
                2-salesperson deals credit each rep ½ unit).
                Active Leads: leads in Active status from the last 90 days. Appts Scheduled includes
                today — today's appointments count there, not in the Last-5 Appts column. Rows with
                a ▸ expand to show scheduled appointment times.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
